import React from 'react'
import {UserManagementService, UserAdmission, ObjectType} from '@oc/user-management'
import {observable, action, makeObservable, toJS} from 'mobx'
import {useAuthInfo} from './Authentication/AuthenticationContext'
import {observer} from 'mobx-react-lite'
import {IMobxToJsConvertible} from '.'
import {definedContextValueHookFactory} from '@oc/hooks'

export interface AllowedObjects {
    allAllowed: boolean
    objectNames: string[]
}

class GlobalLevelResource {
    public constructor(public state: UserPermissionsState, public resource: string) {
        this.canEdit = this.canEdit.bind(this)
        this.canView = this.canView.bind(this)
    }

    public canEdit() {
        return this.state.hasPermission('edit', this.resource)
    }

    public canView() {
        return this.state.hasPermission('view', this.resource)
    }
}

class ViewOnlyAccountLevelResource {
    public constructor(public state: UserPermissionsState, public resource: string) {
        this.canView = this.canView.bind(this)
    }

    public canView(account: string) {
        return this.state.hasPermission('view', this.resource, 'account', account)
    }
}

class AccountLevelResource extends ViewOnlyAccountLevelResource {
    public constructor(state: UserPermissionsState, resource: string) {
        super(state, resource)
        this.canEdit = this.canEdit.bind(this)
    }

    public canEdit(account: string) {
        return this.state.hasPermission('edit', this.resource, 'account', account)
    }
}

class RegionsResource {
    public readonly resource = 'region'

    public constructor(public state: UserPermissionsState) {
        this.canCreateRegion = this.canCreateRegion.bind(this)
        this.canEditDeleteRegion = this.canEditDeleteRegion.bind(this)
        this.canManageAccounts = this.canManageAccounts.bind(this)
        this.canManageSomeRegionAccounts = this.canManageSomeRegionAccounts.bind(this)
    }

    public canCreateRegion() {
        return this.state.hasPermission('create', this.resource)
    }

    public canEditDeleteRegion(region: string) {
        return this.state.hasPermission('edit', this.resource, 'region', region)
    }

    public canManageAccounts(region?: string) {
        return region
            ? this.state.hasPermission('assign-account', this.resource, 'region', region)
            : this.state.hasPermission('assign-account', this.resource)
    }

    public canManageSomeRegionAccounts() {
        return this.state.admissions.some(
            a =>
                a.action === 'assign-account' &&
                a.resource === this.resource &&
                (a.objectType === 'region' || a.objectType === null)
        )
    }
}

class AuditLogResource {
    public readonly resource = 'audit-logs'

    public constructor(public state: UserPermissionsState) {
        this.canView = this.canView.bind(this)
        this.canViewSome = this.canViewSome.bind(this)
    }

    public canView(account?: string) {
        return this.state.hasPermission('view', this.resource, 'account', account)
    }

    public canViewSome() {
        return this.state.admissions.some(
            a =>
                a.action === 'view' &&
                a.resource === this.resource &&
                (a.objectType === 'account' || a.objectType === null)
        )
    }
}

class UserResource {
    public readonly resource = 'user'

    public constructor(public state: UserPermissionsState) {
        this.canCreateUser = this.canCreateUser.bind(this)
        this.canDeleteUser = this.canDeleteUser.bind(this)
        this.canEditUsers = this.canEditUsers.bind(this)
        this.canSignoutUser = this.canSignoutUser.bind(this)
    }

    public canCreateUser() {
        return this.state.hasPermission('create', this.resource)
    }

    public canDeleteUser() {
        return this.state.hasPermission('delete', this.resource)
    }

    public canEditUsers() {
        return this.state.hasPermission('edit', this.resource)
    }

    public canSignoutUser() {
        return this.state.hasPermission('signout', this.resource)
    }
}

class GroupResource {
    public readonly resource = 'group'

    public constructor(public state: UserPermissionsState) {
        this.hasGroupsToAssign = this.hasGroupsToAssign.bind(this)
        this.canAssignGroup = this.canAssignGroup.bind(this)
        this.canEditGroup = this.canEditGroup.bind(this)
    }

    public hasGroupsToAssign() {
        return this.state.admissions.some(a => a.action === 'assign-user' && a.resource === this.resource)
    }

    public canAssignGroup(groupName: string) {
        return this.state.hasPermission('assign-user', this.resource, 'group', groupName)
    }

    public canCreateGroup() {
        return this.state.hasPermission('edit', 'group', 'group')
    }

    public canEditGroup(groupName: string) {
        return this.state.hasPermission('edit', this.resource, 'group', groupName)
    }
}

class AccountResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'account')
        this.canCreateAccount = this.canCreateAccount.bind(this)
        this.canDeleteAccount = this.canDeleteAccount.bind(this)
    }

    public canCreateAccount() {
        return this.state.admissions.some(
            a =>
                a.action === 'create' &&
                a.resource === this.resource &&
                (a.objectType === 'region' || a.objectType === null)
        )
    }

    public canDeleteAccount() {
        return this.state.admissions.some(
            a =>
                a.action === 'delete' &&
                a.resource === this.resource &&
                (a.objectType === 'region' || a.objectType === null)
        )
    }

    public canCreate(region: string) {
        return this.state.hasPermission('create', 'account', 'region', region)
    }

    public canCreateSome() {
        return this.state.admissions.some(
            a =>
                a.action === 'create' &&
                a.resource === this.resource &&
                (a.objectType === 'region' || a.objectType === null)
        )
    }

    public canDelete(account: string) {
        return this.state.hasPermission('delete', 'account', 'account', account)
    }
}

class InterfacesResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'interfaces')
        this.canDownload = this.canDownload.bind(this)
    }

    public canDownload(account: string) {
        return this.state.hasPermission('download', this.resource, 'account', account)
    }
}

class RegisterResource extends ViewOnlyAccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'register')
        this.canCreate = this.canCreate.bind(this)
    }

    public canCreate(account: string) {
        return this.state.hasPermission('create', this.resource, 'account', account)
    }
}

class DeployResource extends ViewOnlyAccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'deploy')
        this.canStart = this.canStart.bind(this)
        this.canValidate = this.canValidate.bind(this)
        this.canCancel = this.canCancel.bind(this)
        this.canViewSome = this.canViewSome.bind(this)
        this.canStartSome = this.canStartSome.bind(this)
    }

    public canStart(account: string) {
        return this.state.hasPermission('start', this.resource, 'account', account)
    }

    public canValidate(account: string) {
        return this.state.hasPermission('validate', this.resource, 'account', account)
    }

    public canCancel(account: string) {
        return this.state.hasPermission('cancel', this.resource, 'account', account)
    }

    public canViewSome() {
        return this.state.admissions.some(
            a =>
                a.action === 'view' &&
                a.resource === this.resource &&
                (a.objectType === 'account' || a.objectType === null)
        )
    }

    public canStartSome() {
        return this.state.admissions.some(
            a =>
                a.action === 'start' &&
                a.resource === this.resource &&
                (a.objectType === 'account' || a.objectType === null)
        )
    }
}

class PeriodicOperationsResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'periodic-operations')
        this.canTrigger = this.canTrigger.bind(this)
        this.canDownload = this.canDownload.bind(this)
    }

    public canTrigger(account: string) {
        return this.state.hasPermission('trigger', this.resource, 'account', account)
    }

    public canDownload(account: string) {
        return this.state.hasPermission('download', this.resource, 'account', account)
    }
}

class ConfigFilesResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'config-files')
        this.canDownload = this.canDownload.bind(this)
    }

    public canDownload(account: string) {
        return this.state.hasPermission('download', this.resource, 'account', account)
    }
}

class DecommissionResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'decommission')
        this.canStart = this.canStart.bind(this)
    }

    public canStart(account: string) {
        return this.state.hasPermission('start', this.resource, 'account', account)
    }
}

class RkUploadLogsResource extends ViewOnlyAccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'rk-log')
        this.canStart = this.canStart.bind(this)
        this.canDelete = this.canDelete.bind(this)
    }

    public canStart(account: string) {
        return this.state.hasPermission('start-upload', this.resource, 'account', account)
    }

    public canDelete(account: string) {
        return this.state.hasPermission('delete-logs', this.resource, 'account', account)
    }
}

class RkRestartServicesResource {
    public readonly resource = 'rk-service'

    public constructor(public state: UserPermissionsState) {
        this.canRestartService = this.canRestartService.bind(this)
    }

    public canRestartService(account: string) {
        return this.state.hasPermission('restart', this.resource, 'account', account)
    }
}

class RkSsmResource {
    public readonly resource = 'rk-ssm'

    public constructor(public state: UserPermissionsState) {
        this.canStart = this.canStart.bind(this)
    }

    public canStart(account: string) {
        return this.state.hasPermission('start', this.resource, 'account', account)
    }
}

class RkMonitoringResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'rk-monitoring')
        this.canViewSome = this.canViewSome.bind(this)
    }

    public canViewSome() {
        return this.state.admissions.some(
            a =>
                a.action === 'view' &&
                a.resource === this.resource &&
                (a.objectType === 'account' || a.objectType === null)
        )
    }
}

class PasswordsResource extends ViewOnlyAccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'passwords')
        this.canReset = this.canReset.bind(this)
    }

    public canReset(account: string) {
        return this.state.hasPermission('reset', this.resource, ObjectType.Account, account)
    }
}

class AccountInformationExportResource {
    public readonly resource = 'account-information-export'

    public constructor(public state: UserPermissionsState) {
        this.canStart = this.canStart.bind(this)
    }

    public canStart() {
        return this.state.hasPermission('start', this.resource)
    }
}

class RoleLevelResource {
    public constructor(public state: UserPermissionsState, public resource: string) {
        this.canEditRole = this.canEditRole.bind(this)
        this.listEditableRoles = this.listEditableRoles.bind(this)
    }

    public canEditRole(roleName: string): boolean {
        return this.state.hasPermission('edit', this.resource, ObjectType.Role, roleName)
    }

    public listEditableRoles(): Set<string> {
        return new Set(this.state.getAllowedObjects(ObjectType.Role, 'edit', this.resource).objectNames)
    }
}

class ArtifactDeliveryResource extends ViewOnlyAccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'artifact-delivery')
        this.canStart = this.canStart.bind(this)
        this.canCancel = this.canCancel.bind(this)
    }

    public canStart(account: string) {
        return this.state.hasPermission('start', this.resource, ObjectType.Account, account)
    }

    public canCancel(account: string) {
        return this.state.hasPermission('cancel', this.resource, ObjectType.Account, account)
    }
}

class OpcUaLibraryHandlerResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'opc-ua-library-handler')
        this.canRunImport = this.canRunImport.bind(this)
        this.canSubscribe = this.canSubscribe.bind(this)
    }

    public canRunImport(account: string) {
        return this.state.hasPermission('import', this.resource, 'account', account)
    }

    public canSubscribe(account: string) {
        return this.state.hasPermission('subscribe', this.resource, 'account', account)
    }
}

class OffboardingResource extends AccountLevelResource {
    constructor(state: UserPermissionsState) {
        super(state, 's2a-user')
    }

    public canOffboard(accountId: string): boolean {
        return this.state.hasPermission('offboard', this.resource, ObjectType.Account, accountId)
    }
}

class SfgDocumentationResource extends AccountLevelResource {
    public constructor(state: UserPermissionsState) {
        super(state, 'sfg-documentation')
        this.canTrigger = this.canTrigger.bind(this)
    }

    public canTrigger(account: string) {
        return this.state.hasPermission('trigger', this.resource, 'account', account)
    }
}

class EdgeStackUpgradeResource extends ViewOnlyAccountLevelResource {
    constructor(state: UserPermissionsState) {
        super(state, 'edge-stack-upgrade')
    }

    public canStart(accountId: string): boolean {
        return this.state.hasPermission('start', this.resource, ObjectType.Account, accountId)
    }
}

export class UserPermissionsState implements IMobxToJsConvertible<UserPermissionsState> {
    @observable isLoading = true
    @observable userId = ''

    private service: UserManagementService
    @observable private userAdmissions: UserAdmission[] = []

    public auditLogs = new AuditLogResource(this)
    public regions = new RegionsResource(this)
    public uoms = new GlobalLevelResource(this, 'uom')
    public users = new UserResource(this)
    public groups = new GroupResource(this)
    public accounts = new AccountResource(this)
    public shifts = new AccountLevelResource(this, 'shifts')
    public networks = new AccountLevelResource(this, 'network')
    public machinesAndServices = new AccountLevelResource(this, 'machines-and-services')
    public equipment = new AccountLevelResource(this, 'equipment')
    public interfaces = new InterfacesResource(this)
    public registrations = new RegisterResource(this)
    public deployments = new DeployResource(this)
    public periodicOperations = new PeriodicOperationsResource(this)
    public passwords = new PasswordsResource(this)
    public configFiles = new ConfigFilesResource(this)
    public decommissioning = new DecommissionResource(this)
    public timestealers = new AccountLevelResource(this, 'timestealers')
    public rkUploadLogs = new RkUploadLogsResource(this)
    public rkRestartServices = new RkRestartServicesResource(this)
    public dasDiagnostic = new ViewOnlyAccountLevelResource(this, 'das-diagnostic')
    public rkMonitoringConfiguration = new AccountLevelResource(this, 'rk-monitoring-config')
    public ntpDiagnostic = new ViewOnlyAccountLevelResource(this, 'ntp-diagnostic')
    public rkMonitoringInfo = new GlobalLevelResource(this, 'rk-monitoring-info')
    public rkMonitoring = new RkMonitoringResource(this)
    public rkSsm = new RkSsmResource(this)
    public rkSsmCommand = new GlobalLevelResource(this, 'rk-ssm-command')
    public insight = new AccountLevelResource(this, 'insight')
    public localEnumerations = new AccountLevelResource(this, 'local-enumeration')
    public dataSources = new AccountLevelResource(this, 'datasource')
    public datapoints = new AccountLevelResource(this, 'datapoint')
    public messages = new AccountLevelResource(this, 'message')
    public dataKeys = new GlobalLevelResource(this, 'datakey')
    public globalLanguages = new GlobalLevelResource(this, 'global-languages')
    public regionGroupTemplate = new GlobalLevelResource(this, 'region-group-template')
    public globalEnumerations = new GlobalLevelResource(this, 'global-enumeration')
    public accountProperties = new GlobalLevelResource(this, 'account-properties')
    public zaitGlobalSettings = new GlobalLevelResource(this, 'zait-global-settings')
    public rkConnectivityCheck = new ViewOnlyAccountLevelResource(this, 'rk-connectivity-check')
    public rkInfo = new AccountLevelResource(this, 'rk-info')
    public logbook = new AccountLevelResource(this, 'logbook')
    public dataValidation = new ViewOnlyAccountLevelResource(this, 'data-validation')
    public accountInformationExport = new AccountInformationExportResource(this)
    public whitelistTemplates = new GlobalLevelResource(this, 'whitelist-templates')
    public reasonCodes = new GlobalLevelResource(this, 'reason-codes')
    public copyAssetsWizard = new AccountLevelResource(this, 'copy-assets')
    public regionTemplateAdmissions = new RoleLevelResource(this, 'region-group-template-admission')
    public contiloop = new AccountLevelResource(this, 'contiloop')
    public globalArtifacts = new GlobalLevelResource(this, 'global-artifact')
    public accountArtifacts = new AccountLevelResource(this, 'account-artifact')
    public artifactDelivery = new ArtifactDeliveryResource(this)
    public subscriptions = new AccountLevelResource(this, 'subscription-information')
    public opcUaLibraryHandler = new OpcUaLibraryHandlerResource(this)
    public offboarding = new OffboardingResource(this)
    public kopVersionControl = new AccountLevelResource(this, 'kop-kubernetes')
    public kpiModels = new AccountLevelResource(this, 'kpi-models')
    public kpiModelsGlobal = new GlobalLevelResource(this, 'kpi-models')
    public kopSettings = new GlobalLevelResource(this, 'kop-settings')
    public flashFluidFiles = new GlobalLevelResource(this, 'flash-fluid-files')
    public sfgDocumentation = new SfgDocumentationResource(this)
    public fleetInfo = new AccountLevelResource(this, 'fleet-info')
    public fleetDevices = new AccountLevelResource(this, 'fleet-device')
    public edgeStackVersion = new GlobalLevelResource(this, 'edge-stack-version')
    public edgeStackUpgrade = new EdgeStackUpgradeResource(this)

    public get admissions(): UserAdmission[] {
        return this.userAdmissions
    }

    public constructor(service: UserManagementService) {
        makeObservable(this)
        this.service = service
    }

    public toJS(): UserPermissionsState {
        return {
            ...toJS(this),
            admissions: this.admissions,
            reload: this.reload,
            load: this.load
        }
    }

    @action.bound
    public async load(userName: string): Promise<void> {
        this.isLoading = true
        try {
            this.userId = userName
            this.userAdmissions = await this.service.getUserAdmissions()
        } finally {
            this.isLoading = false
        }
    }

    @action.bound
    public async reload(): Promise<void> {
        if (this.userId) {
            await this.load(this.userId)
            return
        }

        throw Error('Trying to reload not loaded state.')
    }

    public hasPermission = (
        action: string,
        resource: string,
        objectType?: string | null,
        objectName?: string | null
    ): boolean => {
        return (
            this.userAdmissions &&
            this.userAdmissions.some(
                a =>
                    a.action === action &&
                    a.resource === resource &&
                    ((a.objectName === objectName && a.objectType === objectType) ||
                        (a.objectName === null && a.objectType === null))
            )
        )
    }

    public hasAdmission = (admission: UserAdmission): boolean => {
        const {action, resource, objectType, objectName} = admission
        return this.hasPermission(action, resource, objectType, objectName)
    }

    public hasAdmissionOr = (admissions: UserAdmission[]): boolean => {
        return admissions.some(this.hasAdmission)
    }

    public hasAdmissionAnd = (admissions: UserAdmission[]): boolean => {
        return admissions.every(this.hasAdmission)
    }

    public canEditAdmissions = (): boolean => {
        return this.hasPermission('edit', 'admission', null, null)
    }

    public getAllowedObjects = (objectType: string, action: string, resource: string): AllowedObjects => {
        if (!this.userAdmissions || this.userAdmissions.length === 0) {
            return {
                allAllowed: false,
                objectNames: []
            }
        }

        return {
            allAllowed: this.userAdmissions.some(
                a => a.action === action && a.resource === resource && a.objectName === null && a.objectType === null
            ),
            objectNames: this.userAdmissions
                .filter(ua => ua.action === action && ua.resource === resource && ua.objectType === objectType)
                .map(a => a.objectName as string)
        }
    }
}

const UserPermissionsContext = React.createContext<UserPermissionsState | null>(null)

const _UserPermissionsContextProvider = ({
    children,
    service
}: React.PropsWithChildren<{service: UserManagementService}>) => {
    const [state] = React.useState(new UserPermissionsState(service))
    const {username} = useAuthInfo()

    React.useEffect(() => {
        state.load(username)
    }, [state, username])

    if (state.isLoading) {
        return null
    }

    return <UserPermissionsContext.Provider value={state}>{children}</UserPermissionsContext.Provider>
}

export const UserPermissionsContextProvider = observer(_UserPermissionsContextProvider)

export const useUserPermissionsContext = definedContextValueHookFactory(
    UserPermissionsContext,
    'UserPermissionsContext'
)
